import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BookingTypeToggle, PaymentSection, ModelDayFields, estimateDeposit } from "@/pages/ManualBookingExtras";

/**
 * CLAÈ Admin — Manual Booking modal.
 *
 * Admin creates a booking on behalf of a client using exactly the same
 * backend pipeline as an online booking. The modal lets admin either
 * pick an existing client (search by name/email) or create one inline,
 * select treatment(s), choose date & time, optionally override the
 * availability guard, and (post-save) copy a Stripe deposit link.
 */
export default function AdminManualBooking({ open, onClose, onCreated }) {
  const [treatments, setTreatments] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [newClient, setNewClient] = useState({
    first_name: "", last_name: "", email: "", phone: "", dob: "",
    address: "", postcode: "",
  });
  const [cart, setCart] = useState([]); // [{treatment_id, quantity}]
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [depositLink, setDepositLink] = useState(null);
  const [bookingType, setBookingType] = useState("standard");
  const [method, setMethod] = useState("card");
  const [cashOption, setCashOption] = useState("full");
  const [cashAmount, setCashAmount] = useState("");
  const [modelDays, setModelDays] = useState([]);
  const [dayId, setDayId] = useState("");
  const [slotId, setSlotId] = useState("");
  const [duration, setDuration] = useState("");
  const [modelPrice, setModelPrice] = useState("");

  useEffect(() => {
    if (open) {
      api.get("/treatments/all").then((r) => setTreatments(r.data || [])).catch(() => {});
      const today = new Date().toISOString().slice(0, 10);
      api.get("/admin/model/days").then((r) => setModelDays((r.data || []).filter((d) => d.date >= today))).catch(() => {});
    } else {
      setBookingType("standard"); setMethod("card"); setCashOption("full"); setCashAmount("");
      setDayId(""); setSlotId(""); setDuration(""); setModelPrice("");
      // Reset on close so the next open starts clean.
      setSelectedClient(null); setClientSearch(""); setClientResults([]);
      setNewClient({ first_name:"", last_name:"", email:"", phone:"", dob:"", address:"", postcode:"" });
      setCart([]); setDate(""); setTime(""); setAdminNotes("");
      setAvailableSlots([]); setOverride(false); setResult(null); setDepositLink(null);
    }
  }, [open]);

  // Load /slots when date + duration are known.
  const totalDuration = cart.reduce((acc, c) => {
    const t = treatments.find((t) => t.id === c.treatment_id);
    return acc + (t ? (t.duration_min || 60) * c.quantity : 0);
  }, 0);
  useEffect(() => {
    if (!date || totalDuration <= 0) { setAvailableSlots([]); return; }
    api.get(`/slots?date=${date}&duration=${totalDuration}`)
      .then((r) => setAvailableSlots(r.data?.slots || []))
      .catch(() => setAvailableSlots([]));
  }, [date, totalDuration]);

  const changeType = (t) => {
    setBookingType(t); setCart([]); setDate(""); setTime(""); setDayId(""); setSlotId("");
    setDuration(""); setModelPrice(""); setCashAmount("");
    setCashOption(t === "model" ? "deposit" : "full");
  };
  const currentDay = modelDays.find((d) => d.id === dayId);
  const pickDay = (id) => {
    setDayId(id); setSlotId("");
    setDate(modelDays.find((d) => d.id === id)?.date || "");
  };
  const pickSlot = (id) => {
    setSlotId(id);
    const s = (currentDay?.slots || []).find((x) => x.id === id);
    if (!s) return;
    setCart([{ treatment_id: s.treatment_id, quantity: 1 }]);
    setTime(s.time); setDuration(String(s.duration_min || ""));
    setModelPrice(String(s.model_price ?? ""));
    setCashAmount(s.deposit_amount ? String(s.deposit_amount) : "");
  };
  const pickModelTreatment = (tid) => {
    setCart(tid ? [{ treatment_id: tid, quantity: 1 }] : []);
    const t = treatments.find((x) => x.id === tid);
    const ref = (currentDay?.slots || []).find((s) => s.treatment_id === tid);
    setDuration(String(t?.duration_min || ""));
    setModelPrice(String(ref ? ref.model_price : (t?.price ?? "")));
  };
  const selectedSlot = (currentDay?.slots || []).find((s) => s.id === slotId);
  const defaultDeposit = bookingType === "model" ? Number(selectedSlot?.deposit_amount || 0) : estimateDeposit(cart, treatments);

  const searchClients = useCallback(async (q) => {
    if (!q || q.length < 2) { setClientResults([]); return; }
    try {
      const r = await api.get(`/clients?q=${encodeURIComponent(q)}`);
      setClientResults((r.data || []).slice(0, 8));
    } catch { setClientResults([]); }
  }, []);
  useEffect(() => {
    const t = setTimeout(() => searchClients(clientSearch), 250);
    return () => clearTimeout(t);
  }, [clientSearch, searchClients]);

  const addCartItem = () => setCart([...cart, { treatment_id: "", quantity: 1 }]);
  const removeCartItem = (idx) => setCart(cart.filter((_, i) => i !== idx));
  const updateCartItem = (idx, patch) => setCart(cart.map((c, i) => i === idx ? { ...c, ...patch } : c));

  const submit = async () => {
    if (cart.length === 0 || cart.some((c) => !c.treatment_id)) { toast.error("Choose at least one treatment"); return; }
    if (bookingType === "model" && !dayId) { toast.error("Choose a Model Day"); return; }
    if (!date || !time) { toast.error("Pick a date and time"); return; }
    if (!selectedClient) {
      const req = ["first_name","last_name","email","phone","dob"];
      const missing = req.filter((f) => !newClient[f]);
      if (missing.length) { toast.error(`Missing: ${missing.join(", ")}`); return; }
    }
    setBusy(true);
    try {
      const body = {
        client: selectedClient
          ? { id: selectedClient.id }
          : { ...newClient },
        items: cart,
        date, time,
        admin_notes: adminNotes,
        override_availability: override,
        booking_type: bookingType,
        payment_method: bookingType === "model" ? "cash" : method,
        cash_option: cashOption,
        cash_amount: cashOption === "deposit" && cashAmount !== "" ? Number(cashAmount) : null,
      };
      if (bookingType === "model") {
        Object.assign(body, {
          model_day_id: dayId, slot_id: slotId || null,
          duration_min: duration ? Number(duration) : null,
          model_price: modelPrice !== "" ? Number(modelPrice) : null,
        });
      }
      const r = await api.post("/admin/bookings/manual", body);
      setResult(r.data);
      toast.success(`Booking created · ${r.data.reference}`);
      if (onCreated) onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create booking");
    }
    setBusy(false);
  };

  const generateLink = async () => {
    if (!result?.booking_id) return;
    setBusy(true);
    try {
      const r = await api.get(`/admin/bookings/${result.booking_id}/deposit-link`);
      setDepositLink(r.data);
      if (r.data.checkout_url) toast.success("Deposit link ready — copy & send to client");
      else toast.info(r.data.note || "No link available");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate link");
    }
    setBusy(false);
  };

  const copyLink = () => {
    if (!depositLink?.checkout_url) return;
    navigator.clipboard.writeText(depositLink.checkout_url).then(
      () => toast.success("Link copied"),
      () => toast.error("Could not copy — select the URL manually"),
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6" onClick={onClose} data-testid="manual-booking-modal">
      <div className="bg-white border hairline w-full max-w-3xl mt-10 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl text-stone-900">Manually Book Client</h3>
          <button onClick={onClose} className="mono-label text-xs text-stone-500 hover:text-stone-900">Close ✕</button>
        </div>

        {!result && (
          <div className="mt-5 space-y-5">
            {/* Client */}
            <section>
              <p className="mono-label text-[11px] text-stone-600 mb-2">CLIENT</p>
              {selectedClient ? (
                <div className="border hairline p-3 flex items-center justify-between" data-testid="selected-client">
                  <div>
                    <p className="text-sm font-medium">{selectedClient.first_name} {selectedClient.last_name}</p>
                    <p className="text-xs text-stone-500">{selectedClient.email} · {selectedClient.phone}</p>
                  </div>
                  <button onClick={() => setSelectedClient(null)} className="mono-label text-[11px] text-stone-500 hover:text-stone-900">Change</button>
                </div>
              ) : (
                <>
                  <input
                    data-testid="client-search"
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    placeholder="Search existing client by name or email…"
                    className="border hairline px-3 py-2 text-sm w-full focus:outline-none focus:border-stone-900"
                  />
                  {clientResults.length > 0 && (
                    <ul className="border hairline mt-1 bg-white max-h-56 overflow-y-auto">
                      {clientResults.map((c) => (
                        <li key={c.id}>
                          <button
                            data-testid={`client-result-${c.id}`}
                            type="button"
                            onClick={() => { setSelectedClient(c); setClientResults([]); setClientSearch(""); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-stone-100 border-b hairline"
                          >
                            <span className="font-medium">{c.first_name} {c.last_name}</span>
                            <span className="block text-xs text-stone-500">{c.email}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-[11px] text-stone-500 mt-2">Or create a new client below:</p>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {["first_name","last_name","email","phone","dob"].map((f) => (
                      <input key={f}
                        data-testid={`new-client-${f}`}
                        type={f === "dob" ? "date" : (f === "email" ? "email" : "text")}
                        value={newClient[f]} onChange={(e) => setNewClient({ ...newClient, [f]: e.target.value })}
                        placeholder={f.replace("_"," ")}
                        className="border hairline px-2 py-2 text-sm focus:outline-none focus:border-stone-900" />
                    ))}
                  </div>
                </>
              )}
            </section>

            <BookingTypeToggle value={bookingType} onChange={changeType} />

            {bookingType === "model" && (
              <ModelDayFields days={modelDays} dayId={dayId} setDayId={pickDay} slotId={slotId} onPickSlot={pickSlot}
                treatments={treatments} cart={cart} onTreatment={pickModelTreatment} time={time} setTime={setTime}
                duration={duration} setDuration={setDuration} modelPrice={modelPrice} setModelPrice={setModelPrice} />
            )}

            {/* Treatments */}
            {bookingType === "standard" && (<>
            <section>
              <div className="flex items-center justify-between">
                <p className="mono-label text-[11px] text-stone-600">TREATMENT(S)</p>
                <button data-testid="add-treatment-row" type="button" onClick={addCartItem} className="mono-label text-[11px] border border-stone-900 px-3 py-1 hover:bg-stone-900 hover:text-white transition">+ Add treatment</button>
              </div>
              <div className="mt-2 space-y-2">
                {cart.map((c, i) => (
                  <div key={i} className="grid grid-cols-[1fr_80px_auto] gap-2 items-center">
                    <select data-testid={`treatment-select-${i}`} value={c.treatment_id} onChange={(e) => updateCartItem(i, { treatment_id: e.target.value })}
                            className="border hairline px-2 py-2 text-sm focus:outline-none focus:border-stone-900">
                      <option value="">Select treatment…</option>
                      {treatments.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}{t.price ? ` — £${t.price}` : ""}</option>
                      ))}
                    </select>
                    <input data-testid={`treatment-qty-${i}`} type="number" min="1" value={c.quantity} onChange={(e) => updateCartItem(i, { quantity: Number(e.target.value) || 1 })}
                           className="border hairline px-2 py-2 text-sm focus:outline-none focus:border-stone-900" />
                    <button type="button" onClick={() => removeCartItem(i)} className="mono-label text-[11px] border border-stone-400 text-stone-600 px-3 py-2 hover:bg-stone-100">Remove</button>
                  </div>
                ))}
                {cart.length === 0 && (
                  <p className="text-xs text-stone-500">Add one or more treatments.</p>
                )}
              </div>
            </section>

            {/* Date / time */}
            <section className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="mono-label text-[11px] text-stone-600 block mb-1">DATE</span>
                <input data-testid="booking-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
                       className="border hairline px-3 py-2 text-sm w-full focus:outline-none focus:border-stone-900" />
              </label>
              <label className="block">
                <span className="mono-label text-[11px] text-stone-600 block mb-1">TIME</span>
                {override ? (
                  <input data-testid="booking-time" type="time" value={time} onChange={(e) => setTime(e.target.value)}
                         className="border hairline px-3 py-2 text-sm w-full focus:outline-none focus:border-stone-900" />
                ) : (
                  <select data-testid="booking-time" value={time} onChange={(e) => setTime(e.target.value)}
                          className="border hairline px-3 py-2 text-sm w-full focus:outline-none focus:border-stone-900">
                    <option value="">{availableSlots.length ? "Select…" : "No slots on this date"}</option>
                    {availableSlots.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
              </label>
              <label className="col-span-2 inline-flex items-center gap-2 text-xs text-stone-700">
                <input type="checkbox" data-testid="override-availability" checked={override} onChange={(e) => setOverride(e.target.checked)} />
                Override availability (ignore working-hour / block / existing-appointment rules)
              </label>
            </section>
            </>)}

            {bookingType === "model" && (
              <label className="inline-flex items-center gap-2 text-xs text-stone-700">
                <input type="checkbox" data-testid="model-override-availability" checked={override} onChange={(e) => setOverride(e.target.checked)} />
                Override availability (allow a clash with an existing appointment or block)
              </label>
            )}

            <PaymentSection bookingType={bookingType} method={method} setMethod={setMethod}
              cashOption={cashOption} setCashOption={setCashOption} cashAmount={cashAmount}
              setCashAmount={setCashAmount} defaultDeposit={defaultDeposit} />

            <section>
              <label className="block">
                <span className="mono-label text-[11px] text-stone-600 block mb-1">ADMIN NOTES (OPTIONAL)</span>
                <textarea data-testid="admin-notes" rows={2} value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)}
                          className="border hairline px-3 py-2 text-sm w-full focus:outline-none focus:border-stone-900" />
              </label>
            </section>

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className="mono-label text-xs border border-stone-400 text-stone-700 px-4 py-2 hover:bg-stone-100">Cancel</button>
              <button data-testid="manual-booking-submit" onClick={submit} disabled={busy} style={{ color: "#fff" }}
                      className="mono-label text-xs border border-stone-900 bg-stone-900 text-white px-4 py-2 hover:bg-stone-700 disabled:opacity-50 transition">
                {busy ? "Creating…" : "Create Booking"}
              </button>
            </div>
          </div>
        )}

        {result && result.payment_method === "cash" && (
          <div className="mt-5 space-y-4" data-testid="manual-booking-result">
            <div className="border hairline bg-emerald-50 p-4">
              <p className="mono-label text-emerald-800 text-[11px]">{result.booking_type === "model" ? "MODEL DAY BOOKING CREATED" : "CASH BOOKING CREATED"}</p>
              <p className="text-sm text-emerald-900 mt-1" data-testid="cash-result-summary">
                Ref <strong>{result.reference}</strong> · total <strong>£{Number(result.final_total).toFixed(2)}</strong> · paid <strong>£{Number(result.amount_paid).toFixed(2)}</strong> · outstanding <strong>£{Number(result.remaining_balance).toFixed(2)}</strong> · {result.payment_status}
              </p>
              <p className="text-[11px] text-emerald-800 mt-1">Confirmation email sent · Consent form: <strong>{result.consent_email_sent ? "sent" : "not required for this treatment"}</strong></p>
            </div>
            <div className="flex justify-end">
              <button onClick={onClose} data-testid="manual-booking-done" className="mono-label text-xs border border-stone-900 px-4 py-2 hover:bg-stone-900 hover:text-white transition">Done</button>
            </div>
          </div>
        )}

        {result && result.payment_method !== "cash" && (
          <div className="mt-5 space-y-4" data-testid="manual-booking-result">
            <div className="border hairline bg-emerald-50 p-4">
              <p className="mono-label text-emerald-800 text-[11px]">BOOKING CREATED</p>
              <p className="text-sm text-emerald-900 mt-1">
                Ref <strong>{result.reference}</strong> · deposit <strong>£{Number(result.deposit_required).toFixed(2)}</strong> · {result.payment_status}
              </p>
              <p className="text-[11px] text-emerald-800 mt-1">
                Payment link: {result.payment_link_emailed ? <strong>emailed to client</strong> : "available below (copy & send manually)"} · Consent form: <strong>will send automatically once deposit is paid</strong>
              </p>
            </div>

            <div className="border hairline p-4">
              <p className="mono-label text-[11px] text-stone-600 mb-2">STRIPE DEPOSIT LINK</p>
              {!depositLink ? (
                <button data-testid="generate-link-btn" onClick={generateLink} disabled={busy}
                        className="mono-label text-xs border border-stone-900 bg-stone-900 text-white px-4 py-2 hover:bg-stone-700 disabled:opacity-50 transition">
                  {busy ? "Generating…" : (result.checkout_url ? "View / regenerate payment link" : "Generate Deposit Payment Link")}
                </button>
              ) : depositLink.checkout_url ? (
                <>
                  <input readOnly value={depositLink.checkout_url}
                         data-testid="deposit-link-url"
                         className="border hairline px-2 py-2 text-xs w-full font-mono select-all"
                         onFocus={(e) => e.target.select()} />
                  <div className="flex justify-end mt-2">
                    <button data-testid="copy-link-btn" onClick={copyLink} className="mono-label text-xs border border-stone-900 px-3 py-1 hover:bg-stone-900 hover:text-white">Copy link</button>
                  </div>
                  <p className="text-[11px] text-stone-500 mt-2">Send this link to your client via WhatsApp, text or email. The booking will auto-update to <strong>Deposit Paid</strong> as soon as Stripe confirms payment.</p>
                </>
              ) : (
                <p className="text-xs text-stone-700">{depositLink.note}</p>
              )}
            </div>

            <div className="flex justify-end">
              <button onClick={onClose} className="mono-label text-xs border border-stone-900 px-4 py-2 hover:bg-stone-900 hover:text-white transition">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
